import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import { cities } from '../../data/impactsData';

const HomeView = () => {
  const { isAuthenticated, openAuthModal, setPendingCitySelection } = useAuth();
  const { selectCity } = useDashboard();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4); // Number of cities visible at once
  const carouselRef = useRef(null);
  const carouselInnerRef = useRef(null);
  const isTransitioningRef = useRef(false);
  const isInitialMountRef = useRef(true);
  
  // Create duplicated array for infinite loop
  const duplicatedCities = [...cities, ...cities, ...cities];

  // Responsive items per view
  useEffect(() => {
    const updateItemsPerView = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setItemsPerView(2);
      } else if (width < 1024) {
        setItemsPerView(3);
      } else {
        setItemsPerView(4);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const handleCityClick = (cityName) => {
    if (!isAuthenticated) {
      setPendingCitySelection(cityName);
      openAuthModal('login', cityName);
      return;
    }
    selectCity(cityName);
  };

  const handlePrev = () => {
    if (isTransitioningRef.current) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (isTransitioningRef.current) return;
    setCurrentIndex((prev) => prev + 1);
  };

  // Initialize carousel to start at Delhi (index 0) on mount
  useEffect(() => {
    setCurrentIndex(0); // Start at Delhi (first city)
  }, []);

  // Reset index when itemsPerView changes - Start at Delhi (index 0)
  useEffect(() => {
    setCurrentIndex(0); // Start at Delhi (first city)
    // Reset carousel position immediately
    if (carouselInnerRef.current) {
      carouselInnerRef.current.style.transition = 'none';
      carouselInnerRef.current.style.transform = 'translate3d(0px, 0, 0)';
    }
  }, [itemsPerView]);

  // Smooth scroll to current index using transform with infinite loop
  useEffect(() => {
    if (carouselRef.current && carouselInnerRef.current) {
      // Only set transitioning flag if it's not the initial mount
      // This prevents buttons from being disabled on launch
      if (!isInitialMountRef.current) {
        isTransitioningRef.current = true;
      }
      
      // Double RAF for better synchronization and layout completion
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (carouselRef.current && carouselInnerRef.current) {
            const containerWidth = carouselRef.current.offsetWidth;
            const gap = 16; // 1rem = 16px
            const padding = 48; // 3rem = 48px on each side
            const scrollableWidth = containerWidth - (padding * 2);
            const cardWidth = (scrollableWidth - (gap * (itemsPerView - 1))) / itemsPerView;
            const translateX = -(currentIndex * (cardWidth + gap));
            
            // On initial mount, position without transition
            if (isInitialMountRef.current) {
              carouselInnerRef.current.style.transition = 'none';
              carouselInnerRef.current.style.transform = `translate3d(${translateX}px, 0, 0)`;
              // Mark initial mount as complete after positioning
              setTimeout(() => {
                isInitialMountRef.current = false;
              }, 0);
            } else {
              // Use transform for hardware-accelerated smooth animation
              carouselInnerRef.current.style.transition = 'transform 0.7s cubic-bezier(0.25, 0.1, 0.25, 1)';
              carouselInnerRef.current.style.transform = `translate3d(${translateX}px, 0, 0)`;
              
              // Reset transition flag after animation completes
              setTimeout(() => {
                isTransitioningRef.current = false;
                
                // Handle infinite loop boundaries
                const citiesLength = cities.length;
                if (currentIndex < 0) {
                  // Wrap to end of first section
                  const newIndex = citiesLength - 1;
                  const newTranslateX = -(newIndex * (cardWidth + gap));
                  carouselInnerRef.current.style.transition = 'none';
                  carouselInnerRef.current.style.transform = `translate3d(${newTranslateX}px, 0, 0)`;
                  setCurrentIndex(newIndex);
                } else if (currentIndex >= citiesLength * 2) {
                  // Wrap to beginning (Delhi)
                  const newIndex = 0;
                  const newTranslateX = -(newIndex * (cardWidth + gap));
                  carouselInnerRef.current.style.transition = 'none';
                  carouselInnerRef.current.style.transform = `translate3d(${newTranslateX}px, 0, 0)`;
                  setCurrentIndex(newIndex);
                }
              }, 700); // Match transition duration
            }
          }
        });
      });
    }
  }, [currentIndex, itemsPerView]);

  // Normalize currentIndex to get the actual city index (handle infinite loop)
  const getNormalizedIndex = (index) => {
    const citiesLength = cities.length;
    let normalized = index % citiesLength;
    if (normalized < 0) {
      normalized += citiesLength;
    }
    return normalized;
  };
  
  const normalizedIndex = getNormalizedIndex(currentIndex);
  
  // Handle dot click to jump to specific city
  const handleDotClick = (cityIndex) => {
    if (isTransitioningRef.current) return;
    setCurrentIndex(cityIndex);
  };

  return (
    <section id="view-home">
      <h1 className="landing-heading" style={{
        textAlign: 'center',
        margin: '0 0 1rem 0',
        fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
        fontWeight: 700,
        color: 'var(--primary)',
        letterSpacing: '0.05em'
      }}>
        HEAT STRESS REDUCTION
      </h1>
      <div className="intro-section">
        <p style={{ textAlign: 'justify' }}>
          Heat stress affects cities through multiple, interconnected pathways 
          that disrupt livelihoods, essential services, and human well-being. 
          Extreme heat reduces labour productivity and incomes, strains infrastructure and power systems, 
          intensifies water scarcity, and amplifies urban heat island effects in densely built areas. 
          Vulnerable populations, including low-income households, outdoor workers, and the homeless, 
          face disproportionate exposure, while education systems and public health services 
          experience increased disruptions, illnesses, and emergency demand. 
          This platform assesses four key impact domains through city-specific indicators 
          to enable a comprehensive, evidence-based understanding of heat stress risks.
        </p>
      </div>

      <div style={{ position: 'relative', width: '100%', padding: '0.15rem 1rem 1rem 1rem' }}>
        <div
          ref={carouselRef}
          className="city-carousel"
          style={{
            overflowX: 'hidden',
            padding: '0 3rem',
            position: 'relative'
          }}
        >
          <div
            ref={carouselInnerRef}
            className="city-carousel-inner"
            style={{
              display: 'flex',
              gap: '1rem',
              transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              willChange: 'transform'
            }}
          >
            {duplicatedCities.map((city, index) => (
              <button
                key={`${city.name}-${index}`}
                className="city-card"
                onClick={() => handleCityClick(city.name)}
                style={{
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.6)), url('${process.env.PUBLIC_URL}/${city.image}')`,
                  minWidth: `calc((100% - ${(itemsPerView - 1) * 1}rem) / ${itemsPerView})`,
                  flexShrink: 0
                }}
                aria-label={`Select ${city.name}`}
              >
                {city.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pagination Dots - One dot per city */}
      <div className="carousel-pagination-wrapper">
        <button
          onClick={handlePrev}
          disabled={isTransitioningRef.current}
          className="carousel-nav-arrow carousel-nav-arrow-left"
          aria-label="Previous city"
        >
          ‹
        </button>
        <div className="carousel-pagination">
          {cities.map((city, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              disabled={isTransitioningRef.current}
              className={`carousel-dot ${normalizedIndex === index ? 'active' : ''}`}
              aria-label={`Go to ${city.name}`}
            />
          ))}
        </div>
        <button
          onClick={handleNext}
          disabled={isTransitioningRef.current}
          className="carousel-nav-arrow carousel-nav-arrow-right"
          aria-label="Next city"
        >
          ›
        </button>
      </div>

      <div style={{
        textAlign: 'center',
        marginTop: '0.5rem',
        padding: '0.5rem 0.75rem',
        fontSize: '0.85rem',
        color: '#666',
        fontStyle: 'italic'
      }}>
        All city images displayed on this page are sourced from the internet and are used for illustrative purposes only.
      </div>
    </section>
  );
};

export default HomeView;

